import React, { useRef, useState, useEffect } from "react";

/*************************************************************************
 * ResponsiveContainer Component
 * 
 * A container that adjusts the number of grid columns based on its width.
 * Utilizes ResizeObserver to monitor size changes and apply appropriate styles.
 * The dynamic classes used for grid columns need to be safelisted in tailwind.config.js.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to be rendered inside the container.
 * @param {string} [props.label] - Optional label to display as a title in bold small text.
 * @param {Array} [props.columnsConfig] - Optional configuration for grid columns based on width.
 *                                       Each object should have a `minWidth` and `columns` property.
 *                                       Example:
 *                                       [
 *                                         { minWidth: 0, columns: 1 },
 *                                         { minWidth: 640, columns: 2 },
 *                                         { minWidth: 768, columns: 3 },
 *                                       ]
 * @param {string} [props.justifyItems] - Tailwind CSS class to justify grid items.
 *                                        Examples: 'justify-start', 'justify-center', 'justify-end'.
 * @param {string} [props.alignItems] - Tailwind CSS class to align grid items.
 *                                     Examples: 'items-start', 'items-center', 'items-end'.
 */
const ResponsiveContainer = ({
  children,
  label,
  columnsConfig,
  justifyItems = "justify-start",
  alignItems = "items-end"
}) => {

  // Reference to the container DOM element
  const containerRef = useRef(null);

  // State to manage the number of grid columns; defaults to single column
  const [columns, setColumns] = useState("grid-cols-1"); // Default class

  useEffect(() => {
    // Define default columns configuration if none is provided via props
    const defaultColumnsConfig = [
      { minWidth: 0, columns: 1 },    // Mobile devices
      { minWidth: 640, columns: 2 },  // Small tablets
      { minWidth: 768, columns: 3 },  // Tablets and larger devices
      { minWidth: 1024, columns: 4 }, // Desktops
    ];

    // Use the provided columnsConfig prop or fallback to default configuration
    const config = columnsConfig || defaultColumnsConfig;

    // Sort the configuration by minWidth in ascending order to ensure proper evaluation
    const sortedConfig = [...config].sort((a, b) => a.minWidth - b.minWidth);

    // Initialize a new ResizeObserver to monitor size changes of the container
    const observer = new ResizeObserver((entries) => {
      // Get the width of the container from the first entry
      const width = entries[0].contentRect.width;

      // Determine the appropriate number of columns based on the current width
      let matchedColumns = sortedConfig[0].columns; // Default to the first config

      for (let i = 0; i < sortedConfig.length; i++) {
        if (width >= sortedConfig[i].minWidth) {
          matchedColumns = sortedConfig[i].columns;
        } else {
          break;
        }
      }
      // Update the `columns` state with the corresponding Tailwind CSS class
      setColumns(`grid-cols-${matchedColumns}`);
      console.log("ResponsiveContainer", label, " - Matched Columns", matchedColumns);
      console.log("ResponsiveContainer", label, " - Columns", columns);
    });

    // Start observing the container if the reference is available
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Cleanup function to unobserve the container when the component unmounts
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [columnsConfig]); // Re-run the effect if columnsConfig changes

  return (
    <div ref={containerRef} className="w-full h-auto overflow-auto border p-4">
      {/* 
        Inner div that applies the grid layout.
        The number of columns adjusts based on the  `columns` state.
        Justification and alignment of grid items are customizable via props.
      */}
      {label && <span className="font-bold text-sm">{label}</span>}
      <div className={`grid gap-1 ${columns} ${justifyItems} ${alignItems}`}>
        {children}
      </div>
    </div>
  );
};

export default ResponsiveContainer;